import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product barcode (UPC) from the selected variant.
 * Listens for variant update events and updates the barcode value without reusing SKU logic.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} barcodeContainer - The container element for the barcode row
 * @property {HTMLElement} productVariantBarcodeValue - The element that displays only the barcode value (label stays outside this node)
 *
 * @extends {Component<Refs>}
 */
class ProductBarcodeComponent extends Component {
  requiredRefs = ['barcodeContainer', 'productVariantBarcodeValue'];

  connectedCallback() {
    super.connectedCallback();
    const variantUpdateTarget = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!variantUpdateTarget) return;
    variantUpdateTarget.addEventListener(ThemeEvents.variantUpdate, this.updateProductVariantBarcode);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const variantUpdateTarget = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!variantUpdateTarget) return;
    variantUpdateTarget.removeEventListener(ThemeEvents.variantUpdate, this.updateProductVariantBarcode);
  }

  /**
   * Updates the barcode from the variant payload on variant:update.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateProductVariantBarcode = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    }

    if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    if (event.detail.resource) {
      const productVariantBarcode =
        event.detail.resource.barcode != null ? String(event.detail.resource.barcode) : '';

      if (productVariantBarcode) {
        this.style.display = 'block';
        this.refs.productVariantBarcodeValue.textContent = productVariantBarcode;
      } else {
        this.style.display = 'none';
        this.refs.productVariantBarcodeValue.textContent = '';
      }
    }
  };
}

if (!customElements.get('product-barcode-component')) {
  customElements.define('product-barcode-component', ProductBarcodeComponent);
}
